import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachMoney,
  Block,
  CheckCircle,
  ExpandLess,
  ExpandMore,
  Payment,
  Refresh,
} from '@mui/icons-material';
import adminService from '../services/adminService';

const STATUS_COLORS = { owed: 'warning', paid: 'success', void: 'default' };

const PAYMENT_METHODS = ['EcoCash', 'Bank Transfer', 'Cash', 'Other'];

const fmtAmount = (n) => `$${parseFloat(n).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

// ── Record Payment Dialog ────────────────────────────────────────────────────
const RecordPaymentDialog = ({ open, earningIds, onClose, onSuccess }) => {
  const [form, setForm] = useState({ method: 'EcoCash', reference: '', paidAt: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.method) { setError('Payment method is required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        earningIds,
        method: form.method,
        ...(form.reference ? { reference: form.reference } : {}),
        ...(form.paidAt ? { paidAt: form.paidAt } : {}),
        ...(form.notes ? { notes: form.notes } : {}),
      };
      const res = await adminService.recordVerifierPayout(payload);
      onSuccess(res.message || `${earningIds.length} earning(s) marked paid`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => { setForm({ method: 'EcoCash', reference: '', paidAt: '', notes: '' }); setError(''); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record Payment — {earningIds.length} earning(s)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <FormControl fullWidth required>
            <InputLabel>Payment method</InputLabel>
            <Select
              value={form.method}
              label="Payment method"
              onChange={(e) => setForm((p) => ({ ...p, method: e.target.value }))}
            >
              {PAYMENT_METHODS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            label="Reference (optional)"
            value={form.reference}
            onChange={(e) => setForm((p) => ({ ...p, reference: e.target.value }))}
            fullWidth
            helperText="Transaction ID, receipt number, etc."
          />
          <TextField
            label="Payment date (optional)"
            type="date"
            value={form.paidAt}
            onChange={(e) => setForm((p) => ({ ...p, paidAt: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={<CheckCircle />}>
          {saving ? 'Saving…' : 'Mark as Paid'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Void Dialog ───────────────────────────────────────────────────────────────
const VoidDialog = ({ open, earningId, onClose, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleVoid = async () => {
    setSaving(true);
    setError('');
    try {
      await adminService.voidVerifierEarning(earningId, reason || undefined);
      onSuccess('Earning voided');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to void earning');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => { setReason(''); setError(''); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Void Earning</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" color="error" onClick={handleVoid} disabled={saving} startIcon={<Block />}>
          {saving ? 'Voiding…' : 'Void'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Verifier Group Row ────────────────────────────────────────────────────────
const VerifierGroupRow = ({ group, selected, onToggleAll, onToggleOne, onVoid }) => {
  const [open, setOpen] = useState(true);
  const owedEarnings = group.earnings.filter((e) => e.status === 'owed');
  const owedIds = owedEarnings.map((e) => e.id);
  const allOwedSelected = owedIds.length > 0 && owedIds.every((id) => selected.has(id));
  const someOwedSelected = owedIds.some((id) => selected.has(id));

  return (
    <>
      {/* Verifier summary row */}
      <TableRow
        sx={{ bgcolor: 'action.hover', cursor: 'pointer' }}
        onClick={() => setOpen((p) => !p)}
      >
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          {owedIds.length > 0 && (
            <Checkbox
              indeterminate={someOwedSelected && !allOwedSelected}
              checked={allOwedSelected}
              onChange={() => onToggleAll(owedIds)}
            />
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton size="small">{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
            <Box>
              <Typography variant="subtitle2">{group.verifier_name || '(unknown)'}</Typography>
              <Typography variant="caption" color="text.secondary">{group.verifier_email}</Typography>
            </Box>
          </Stack>
        </TableCell>
        <TableCell align="right">
          <Typography variant="subtitle2" color="warning.main">{fmtAmount(group.owed_total)} owed</Typography>
          {group.paid_total > 0 && (
            <Typography variant="caption" color="text.secondary"> / {fmtAmount(group.paid_total)} paid</Typography>
          )}
        </TableCell>
        <TableCell align="center">
          <Chip label={`${group.earnings.length} earning(s)`} size="small" />
        </TableCell>
        <TableCell />
      </TableRow>

      {/* Line items */}
      <TableRow>
        <TableCell colSpan={5} sx={{ p: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Status</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Payer</TableCell>
                    <TableCell>Contractor</TableCell>
                    <TableCell>Accrued</TableCell>
                    <TableCell>Paid</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.earnings.map((e) => (
                    <TableRow key={e.id} hover>
                      <TableCell padding="checkbox">
                        {e.status === 'owed' && (
                          <Checkbox
                            checked={selected.has(e.id)}
                            onChange={() => onToggleOne(e.id)}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={e.status} color={STATUS_COLORS[e.status] || 'default'} size="small" />
                      </TableCell>
                      <TableCell>{fmtAmount(e.amount)}</TableCell>
                      <TableCell>{e.payer}</TableCell>
                      <TableCell>
                        {e.contractor_first_name
                          ? `${e.contractor_first_name} ${e.contractor_last_name || ''}`.trim()
                          : <Typography variant="caption" color="text.secondary">—</Typography>}
                      </TableCell>
                      <TableCell>{fmtDate(e.created_at)}</TableCell>
                      <TableCell>{fmtDate(e.paid_at)}</TableCell>
                      <TableCell>{e.method || '—'}</TableCell>
                      <TableCell>
                        {e.status !== 'void' && (
                          <Tooltip title="Void this earning">
                            <IconButton size="small" color="error" onClick={() => onVoid(e.id)}>
                              <Block fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {e.void_reason && (
                          <Tooltip title={`Void reason: ${e.void_reason}`}>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>ℹ</Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const AdminVerifierPayoutsPage = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Selection
  const [selected, setSelected] = useState(new Set());

  // Dialogs
  const [payDialog, setPayDialog] = useState(false);
  const [voidDialog, setVoidDialog] = useState({ open: false, id: null });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminService.getVerifierPayouts();
      setGroups(data || []);
      setSelected(new Set());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load verifier payouts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleToggleAll = (ids) => setSelected((prev) => {
    const next = new Set(prev);
    const allIn = ids.every((id) => next.has(id));
    ids.forEach((id) => allIn ? next.delete(id) : next.add(id));
    return next;
  });

  const handlePaySuccess = (msg) => {
    setPayDialog(false);
    setSuccess(msg);
    load();
  };

  const handleVoidSuccess = (msg) => {
    setVoidDialog({ open: false, id: null });
    setSuccess(msg);
    load();
  };

  const totalOwed = groups.reduce((sum, g) => sum + g.owed_total, 0);
  const selectedCount = selected.size;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <AttachMoney color="primary" />
        <Typography variant="h4">Verifier Payouts</Typography>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Verifiers earn a fee for each accepted contractor verification. Select owed earnings and record a payment, or void individual items.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Summary bar */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
            <Box>
              <Typography variant="h5" color="warning.main">{fmtAmount(totalOwed)}</Typography>
              <Typography variant="caption" color="text.secondary">Total owed across {groups.length} verifier(s)</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedCount > 0 ? `${selectedCount} earning(s) selected` : 'Select earnings below to record a payment'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                startIcon={<Refresh />}
                onClick={load}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Payment />}
                disabled={selectedCount === 0}
                onClick={() => setPayDialog(true)}
              >
                Record Payment ({selectedCount})
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Grouped table */}
      {groups.length === 0 ? (
        <Alert severity="info">No verifier earnings found.</Alert>
      ) : (
        <TableContainer component={Card} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Verifier</TableCell>
                <TableCell align="right">Outstanding</TableCell>
                <TableCell align="center">Earnings</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => (
                <VerifierGroupRow
                  key={group.verifier_id}
                  group={group}
                  selected={selected}
                  onToggleAll={handleToggleAll}
                  onToggleOne={handleToggleOne}
                  onVoid={(id) => setVoidDialog({ open: true, id })}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialogs */}
      <RecordPaymentDialog
        open={payDialog}
        earningIds={[...selected]}
        onClose={() => setPayDialog(false)}
        onSuccess={handlePaySuccess}
      />
      <VoidDialog
        open={voidDialog.open}
        earningId={voidDialog.id}
        onClose={() => setVoidDialog({ open: false, id: null })}
        onSuccess={handleVoidSuccess}
      />
    </Container>
  );
};

export default AdminVerifierPayoutsPage;
